import { FolderOpen, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";

import { Input } from "@/components/ui/input.tsx";
import { cn } from "@/lib/utils.ts";
import { useArtifactLibrary } from "@/hooks/useArtifactLibrary.ts";
import { libraryEmptyStateText, visibleLibraryItems } from "@/shared/artifactLibrarySharing.ts";
import {
  ARTIFACT_LIBRARY_CHIPS,
  type ArtifactLibraryArtifactItem,
  type ArtifactLibraryChipId,
  type ArtifactLibraryItem,
  type ArtifactLibrarySharingId,
  DEFAULT_LIBRARY_SORT,
  type LibrarySort,
} from "@/types/artifactLibrary.ts";

import { Loader } from "../loader/Loader.tsx";
import { cardListClass } from "../threads/shared.tsx";
import { getThreadPagePath } from "../threads/util.ts";
import { ArtifactLibraryChips } from "./ArtifactLibraryChips.tsx";
import { ArtifactLibraryCard, ArtifactLibraryRow } from "./ArtifactLibraryItemViews.tsx";
import { ArtifactLibraryLightbox } from "./ArtifactLibraryLightbox.tsx";
import {
  librarySortAxis,
  PanelControls,
  type PanelViewMode,
  sharingAxis,
  viewAxis,
} from "@/views/Settings/panelControls.tsx";

/**
 * React key for a result. Namespaced by kind because the two id spaces are
 * unrelated — thread 5 and an artifact whose ent id is "5" are different rows.
 */
const itemKey = (item: ArtifactLibraryItem): string => {
  if (item.itemKind === "thread") return `thread-${item.threadId}`;
  if (item.itemKind === "sharedArtifact") return `shared-${item.id}`;
  return `artifact-${item.id}`;
};

/**
 * The Artifact Library content: search box, filter chips, sort + view menu, the
 * infinite-scrolling results, and the artifact lightbox. Lives apart from the
 * route view so it stays free of app-shell imports (and is unit-testable).
 */
export const ArtifactLibraryBody = ({
  scrollRoot,
  onNavigate,
  viewToggleSlot,
  className,
  controls,
  offerSharing = false,
}: {
  /** Merged onto the root, e.g. to drop the top padding under a host header. */
  className?: string;
  /**
   * Controlled search, chip filter, and view mode, for a host whose header
   * owns those controls (the Settings tab). When passed, the body renders no
   * search box, chip row, or menu of its own — only the results. Omitted, the
   * body keeps its internal controls (the search dialog).
   *
   * Sort is included: a host that owns the menu owns the sort control in it,
   * and the body has to search in the order that control is showing. Leaving it
   * out stranded the axis entirely once every host began supplying controls —
   * the menu offered no sort, and the body quietly searched by relevance.
   */
  controls?: {
    searchInput: string;
    activeChipId: ArtifactLibraryChipId;
    viewMode: PanelViewMode;
    sort: LibrarySort;
    /**
     * Undefined means the host offers no sharing axis. Distinct from `all`,
     * which asks for shared items.
     */
    sharingId?: ArtifactLibrarySharingId;
  };
  /**
   * Whether an uncontrolled host offers the Sharing axis, which the body then
   * adds to its own menu. Ignored alongside `controls`, which carries its own.
   */
  offerSharing?: boolean;
  /**
   * The scrolling ancestor the infinite-scroll sentinel is measured against.
   * Omit on a page that scrolls itself; a host that puts this body inside its
   * own fixed-height scroll container (the search dialog) must pass one, or the
   * sentinel is measured against the viewport and never fires.
   *
   * Deliberately the element and not a ref: refs attach after render, so a ref
   * would still read `null` here on the render that matters and nothing would
   * re-run the effect. Hosts should hold it in state via a callback ref.
   */
  scrollRoot?: HTMLElement | null;
  /**
   * Called instead of navigating when a result leads away from this surface — a
   * thread, or a shared artifact — so a host can close itself first. Defaults
   * to navigating.
   */
  onNavigate?: (path: string) => void;
  /**
   * An element in the host's own chrome to render the filter/sort/view menu
   * into, rather than at the end of the filter-chip row. The search dialog
   * passes its header, because the chips scroll horizontally and ran under a
   * parked at the end of that row.
   *
   * Element rather than a boolean because the destination is outside this
   * subtree, and — like `scrollRoot` — held in the host's state via a callback
   * ref, so `null` means "asked for, not mounted yet" while omitting the prop
   * altogether keeps the menu inline.
   */
  viewToggleSlot?: HTMLElement | null;
}) => {
  const [, setLocation] = useLocation();
  const [internalSearchInput, setInternalSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [internalChipId, setInternalChipId] = useState<ArtifactLibraryChipId>("all");
  const searchInput = controls?.searchInput ?? internalSearchInput;
  const activeChipId = controls?.activeChipId ?? internalChipId;
  const [internalViewMode, setInternalViewMode] = useState<PanelViewMode>("list");
  const viewMode = controls?.viewMode ?? internalViewMode;
  // A host with no sharing axis must keep sending nothing rather than `all`.
  const sharingEnabled = controls ? controls.sharingId !== undefined : offerSharing;
  const [internalSharingId, setInternalSharingId] = useState<ArtifactLibrarySharingId>("all");
  // Through the flag, so a choice left behind when the axis goes can't keep narrowing.
  const sharingId = sharingEnabled ? (controls?.sharingId ?? internalSharingId) : "all";
  // Whoever draws the menu owns the axis: the host when it supplies controls,
  // the body's own menu otherwise.
  const [internalSort, setInternalSort] = useState<LibrarySort>(DEFAULT_LIBRARY_SORT);
  const sort = controls ? controls.sort : internalSort;
  const [selected, setSelected] = useState<ArtifactLibraryArtifactItem | null>(null);
  const clearSelected = useCallback(() => setSelected(null), []);

  // A thread navigates; an owned artifact opens the lightbox in place; a shared
  // one navigates to the artifact page, whose claimed branch already renders
  // someone else's artifact read-only. The lightbox can't: it mounts
  // thread-scoped chrome, and a shared item carries no thread.
  const handleSelect = useCallback(
    (item: ArtifactLibraryItem) => {
      const navigate = onNavigate ?? setLocation;
      if (item.itemKind === "thread") {
        navigate(getThreadPagePath(item.threadId));
        return;
      }
      if (item.itemKind === "sharedArtifact") {
        navigate(`/artifacts/${item.artifactType}/${item.id}`);
        return;
      }
      setSelected(item);
    },
    [onNavigate, setLocation],
  );

  // From the lightbox to the artifact's thread, by the same route a thread
  // result takes, so the canvas host keeps its trail back to Search. The
  // selection is cleared first: on the canvas the Library stays mounted behind
  // the thread, and the lightbox would otherwise still be open on return.
  const handleOpenThread = useCallback(
    (threadId: number) => {
      setSelected(null);
      (onNavigate ?? setLocation)(getThreadPagePath(threadId));
    },
    [onNavigate, setLocation],
  );

  // Debounce the text input into the query that drives the search.
  useEffect(() => {
    const handle = setTimeout(() => setQuery(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const activeChip =
    ARTIFACT_LIBRARY_CHIPS.find((chip) => chip.id === activeChipId) ?? ARTIFACT_LIBRARY_CHIPS[0];
  // Always send the chip's types, `all` included. Omitting them is the wire
  // signal for "artifacts only" that keeps a pre-threads frontend working, so
  // this is how a current one opts in to thread results. No memo needed —
  // the chip table is a module-level `as const`, so this is already a stable
  // reference.
  const types = activeChip.types;

  const {
    items: fetched,
    isLoading,
    error,
    hasNextPage,
    loadMore,
    isFetchingNextPage,
  } = useArtifactLibrary({
    q: query || undefined,
    types,
    // Sent even for the resting value, because absent means something else on
    // the wire: the pre-sharing contract, which leaves shared items out
    // altogether. `all` is how a current client asks for them alongside its own.
    sharing: sharingEnabled ? sharingId : undefined,
    sort,
  });

  const items = useMemo(() => visibleLibraryItems(fetched, sharingId), [fetched, sharingId]);

  // Infinite scroll: load the next page when the sentinel scrolls into view.
  // `root: null` is the viewport, which is right for a page that scrolls itself.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) loadMore();
      },
      { root: scrollRoot ?? null, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // `scrollRoot` is a dependency so the observer is rebuilt against the host's
    // container once it mounts, rather than staying bound to the viewport.
  }, [hasNextPage, isFetchingNextPage, loadMore, scrollRoot]);

  // The same menu the Settings tab's header shows, so a host that has no header
  // of its own (the search dialog) offers the identical control rather than a
  // segmented toggle that agreed with it only by convention. It carries the
  // sort axis too, which is why `PanelControls` labels its trigger "Filter &
  // Sort" here and "Filter" on the Settings tab.
  const controlsMenu = (
    <PanelControls
      filters={
        controls === undefined && offerSharing
          ? [sharingAxis({ value: sharingId, onChange: setInternalSharingId })]
          : undefined
      }
      sort={librarySortAxis({ value: sort, onChange: setInternalSort })}
      view={viewAxis({
        value: viewMode,
        onChange: setInternalViewMode,
        defaultValue: "list",
        statsigName: "artifact-library-view",
      })}
      subject="library"
      // Predates the Sharing axis; kept so the analytics series stays continuous.
      statsigName="artifact-library-view-menu"
    />
  );
  const hostPlacesMenu = viewToggleSlot !== undefined;

  return (
    <div className={cn("flex flex-col gap-4 py-4", className)}>
      {controls === undefined && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setInternalSearchInput(e.target.value)}
              placeholder="Search all content"
              className="pl-9"
              data-statsig-name="artifact-library-search"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <ArtifactLibraryChips activeChipId={activeChipId} onSelect={setInternalChipId} />
            {hostPlacesMenu ? null : controlsMenu}
          </div>
        </>
      )}
      {viewToggleSlot ? createPortal(controlsMenu, viewToggleSlot) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader />
        </div>
      ) : error ? (
        <div className="py-16 text-center text-muted-foreground">
          <div className="text-lg font-medium">Couldn't load your library</div>
          <div className="text-sm">Try refreshing the page.</div>
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <FolderOpen className="mx-auto mb-3 size-8 opacity-50" />
          <div className="text-sm">{libraryEmptyStateText(sharingId, activeChip)}</div>
        </div>
      ) : viewMode === "list" ? (
        // gap-3 matches the card grid: the rows are white cards now, and
        // touching cards read as one slab.
        <div className={cardListClass}>
          {items.map((item) => (
            <ArtifactLibraryRow key={itemKey(item)} item={item} onSelect={handleSelect} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <ArtifactLibraryCard key={itemKey(item)} item={item} onSelect={handleSelect} />
          ))}
        </div>
      )}

      <div ref={sentinelRef} />
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <Loader />
        </div>
      )}

      {selected && (
        <ArtifactLibraryLightbox
          item={selected}
          onClose={clearSelected}
          onOpenThread={handleOpenThread}
        />
      )}
    </div>
  );
};
